import React from 'react'
import { RealEstateCategory, RealEstateFeatures, SegmentationType, TransactionType } from '../../../../../@types/types'
import { Flow, FlowName, FlowScreenElement } from '../../../../../@types/flow-types'
import { PROPERTY_OWNER_GASTRONOMY_SELL_FLOW_PATH } from '../../../../../services/routes/routes'
import { propertyOwnerGastronomySellSchema } from './propertyOwnerGastronomySellSchema'
import {
  SCREEN_ADDRESS,
  SCREEN_BASIC_INFO,
  SCREEN_CONNECTIONS,
  SCREEN_CONSTRUCTION,
  SCREEN_CONTACT_INFO,
  SCREEN_DESCRIPTION,
  SCREEN_MULTIMEDIA,
  SCREEN_OTHER_FACILITIES,
  SCREEN_REALTOR_LEAD_ENGINE,
  SCREENS,
} from '../../../../../services/domain/wizardScreens'
import ContactInfo from '../../../../../components/pages/insertionPage/screens/contactInfo/ContactInfo'
import Address from '../../../../../components/pages/insertionPage/screens/address/Address'
import Construction from '../../../../../components/pages/insertionPage/screens/construction/Construction'
import Multimedia from '../../../../../components/pages/insertionPage/screens/multimediaNew/Multimedia'

import { MultimediaUploaderType } from '../../../../../@types/multimedia-types'
import Description from '../../../../../components/pages/insertionPage/screens/description/Description'
import OtherFacilities from '../../../../../components/pages/insertionPage/screens/otherFacilities/OtherFacilities'
import {
  estateFeatures,
  freeFrom,
  interiorQuality,
  numberOfBeds,
  numberOfSeats,
  parkingSpace,
  totalFloorsField,
} from '../../../../../components/pages/insertionPage/screens/otherFacilities/wrapper/otherFacilitiesFieldsWrapper'
import { getDefaultClasses } from '../../../../../utils/style-utils'
import { priceToApiSell } from '../../../../../api/realEstate/converters/toApi/estate/price/priceToApi'
import BasicInfo from '../../../../../components/pages/insertionPage/screens/basicInfo/BasicInfo'
import {
  auxiliaryAreaField,
  mainFloorSpaceField,
  priceField,
  purchasePriceOnRequestToggle,
  totalFloorSpaceField,
} from '../../../../../components/pages/insertionPage/screens/basicInfo/wrappers/basicInfoFieldsWrapper'
import { PriceFieldType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/priceField/PriceField'
import { buildingTypeField } from '../../../../../components/pages/insertionPage/screens/commonFields/buildingType/BuildingType'
import { BuildingTypeOption } from '../../../../../@types/dropdown-types'
import { MainFloorType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/mainFloorSpaceField/MainFloorSpaceField'
import Connections from '../../../../../components/pages/insertionPage/screens/connections/Connections'
import { RealtorLeadEngine } from '../../../../../components/pages/insertionPage/screens/realtorLeadEngine/RealtorLeadEngine'

const propertyOwnerGastronomySellFlowScreens: FlowScreenElement[] = [
  {
    screen: SCREENS[SCREEN_ADDRESS],
    element: (props) => <Address {...props} />,
  },
  {
    screen: SCREENS[SCREEN_BASIC_INFO],
    element: (props) => (
      <BasicInfo
        {...props}
        screenTooltipKey='tooltip.screens.basicInfo.gastronomyRentSell'
        properties={[
          totalFloorSpaceField(),
          mainFloorSpaceField(MainFloorType.GASTRONOMY_AREA, 'right', undefined),
          auxiliaryAreaField('left'),
          buildingTypeField(
            'object',
            [
              BuildingTypeOption.BAR_LOUNGE,
              BuildingTypeOption.CAFE,
              BuildingTypeOption.GUESTS_HOUSE,
              BuildingTypeOption.TAVERN,
              BuildingTypeOption.HOTEL,
              BuildingTypeOption.HOTEL_RESIDENCE,
              BuildingTypeOption.HOTEL_GARNI,
              BuildingTypeOption.PENSION,
              BuildingTypeOption.RESTAURANT,
              BuildingTypeOption.BUNGALOW,
            ],
            getDefaultClasses('right')
          ),
          priceField(PriceFieldType.BUY_PRICE, getDefaultClasses('left')),
          freeFrom(getDefaultClasses('right')),
          purchasePriceOnRequestToggle(),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_DESCRIPTION],
    element: (props) => <Description {...props} hasFurnishingNote={true} hasLocationNote={true} hasOtherNote={true} />,
  },
  {
    screen: SCREENS[SCREEN_MULTIMEDIA],
    element: (props) => (
      <Multimedia
        {...props}
        doCloseMultimediaStatusMessage={props.doCloseMultimediaStatusMessage!}
        allowedTypesToDisplay={[
          MultimediaUploaderType.PICTURE,
          MultimediaUploaderType.FLOOR_PLAN,
          MultimediaUploaderType.DOCUMENT,
          MultimediaUploaderType.VIDEO,
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_OTHER_FACILITIES],
    element: (props) => (
      <OtherFacilities
        screenTooltipKey='tooltip.screens.otherFacilities.allSell'
        {...props}
        properties={[
          estateFeatures([
            RealEstateFeatures.BASEMENT,
            RealEstateFeatures.LISTED,
            RealEstateFeatures.PERSON_ELEVATOR,
            RealEstateFeatures.TERRACE,
          ]),
          interiorQuality(),
          numberOfSeats(getDefaultClasses('left')),
          numberOfBeds(getDefaultClasses('right')),
          totalFloorsField(getDefaultClasses('left')),
          parkingSpace({ showParkingSpaceType: false, parkingSpaceRentTypeLabel: 'RentPerItem' }),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_CONNECTIONS],
    element: (props) => <Connections {...props} />,
  },
  {
    screen: SCREENS[SCREEN_CONSTRUCTION],
    element: (props) => <Construction {...props} hasLastModernizationField={true} />,
  },
  {
    screen: SCREENS[SCREEN_REALTOR_LEAD_ENGINE],
    element: (props) => <RealtorLeadEngine {...props} />,
  },
  {
    screen: SCREENS[SCREEN_CONTACT_INFO],
    element: (props) => <ContactInfo {...props} />,
  },
]

export const propertyOwnerGastronomySellFlow: Flow = {
  flowInformation: {
    owner: 'wizard.navigation.header.owner',
    estateType: 'wizard.navigation.header.gastronomy',
    offerType: 'wizard.navigation.header.sells',
    path: PROPERTY_OWNER_GASTRONOMY_SELL_FLOW_PATH,
    name: FlowName.PROPERTY_OWNER_GASTRONOMY_SELL,
    validationSchema: propertyOwnerGastronomySellSchema,
    priceToApiMapper: priceToApiSell,
    metaData: {
      category: RealEstateCategory.GASTRONOMY,
      segmentation: SegmentationType.PROPERTY_OWNER,
      transaction: TransactionType.SELL,
    },
  },
  defaultStateValues: { hideStreetAndStreetNumber: false },
  flowScreens: propertyOwnerGastronomySellFlowScreens,
}
